/**
 * Module-level context the Studio extensions read at renderHTML time.
 * Extensions cannot receive reactive props, so the page sets this ONCE
 * (registry + labels + media resolver) before the editor instance is
 * created; node attrs are never derived from it, so it has no effect on
 * what gets saved.
 */

let context = {
  /** @type {Map<string, object>} code => registry entry */
  registryByCode: new Map(),
  /** @type {Record<string, string>} i18n strings snapshot for renderHTML */
  labels: {},
  /** @type {(mediaPart: string) => string|null} */
  mediaUrl: () => null,
  /** @type {Record<string, string>} media part => SVG of an embedded object's preview (the server converts Word's EMF) */
  objectPreviews: {},
  /** @type {{ fonts: object|null, sheets: Record<string, object> }} edit models of the Excel objects, by preview part (RED-317 PR 3d-B) */
  objectSheets: { fonts: null, sheets: {} },
  /** @type {object|null} the backend Presentation payload (styles/theme/…) */
  presentation: null,
  /** @type {object|null} the section payload (page size + margins, mm) */
  section: null,
  /** @type {((layout: object) => void)|null} pagination → page-chrome bridge */
  onPageLayout: null,
}

export function setStudioContext(next) {
  context = { ...context, ...next }
}

/** Pattern-aware registry lookup: 'qv-foto#3' resolves to the 'qv-foto#' family. */
export function registryEntry(code) {
  // ${/kinnitaja} closes a marker region — valid when the opener is one.
  if (code.startsWith('/')) {
    const opener = context.registryByCode.get(code.slice(1))
    return opener && opener.kind === 'block-marker' ? opener : null
  }

  const exact = context.registryByCode.get(code)
  if (exact) return exact

  for (const entry of context.registryByCode.values()) {
    if (
      entry.isPattern &&
      code.startsWith(entry.code) &&
      /^\d+$/.test(code.slice(entry.code.length))
    ) {
      return entry
    }
  }

  return null
}

export function studioLabel(key) {
  return context.labels[key] ?? key
}

/** @returns {Array<object>} all registry entries (already type-filtered by the load) */
export function registryEntries() {
  return Array.from(context.registryByCode.values())
}

export function mediaUrl(mediaPart) {
  return context.mediaUrl(mediaPart)
}

/** The server-made SVG of an embedded object's preview picture, or null. */
export function objectPreview(mediaPart) {
  const svg = mediaPart ? context.objectPreviews?.[mediaPart] : null
  return typeof svg === 'string' && svg.startsWith('<svg') ? svg : null
}

/** The edit model of the Excel object whose preview is `mediaPart` (RED-317 PR 3d-B), or null. */
export function objectSheet(mediaPart) {
  const sheet = mediaPart ? context.objectSheets?.sheets?.[mediaPart] : null
  return sheet && sheet.version === 1 && sheet.editable ? sheet : null
}

/** The font widths retyped object cells are laid out with (the server's table). */
export function objectFonts() {
  return context.objectSheets?.fonts ?? null
}

/** Table-style definition from the presentation payload (band sizes etc.). */
export function tableStyleOf(styleId) {
  return context.presentation?.styles?.table?.[styleId] ?? null
}

/** The numbering definitions ({nums, abstract}), or null pre-M7.3 payloads. */
export function presentationNumbering() {
  return context.presentation?.numbering ?? null
}

/** Word's default tab interval for the loaded document, twips. */
export function defaultTabStopTw() {
  return context.presentation?.settings?.defaultTabStopTw ?? 708
}

/** Plain text of footnote `id` from the presentation payload, or null.
 *  PHP JSON wobble: a dense id-keyed map may arrive as a JS ARRAY —
 *  string/number indexing works for both, Array methods would not. */
export function footnoteText(id) {
  return context.presentation?.footnotes?.[id] ?? null
}

/**
 * The fragment's paragraph style for a heading level, as the compiler picks
 * it (contract §2): the style named "heading N", else one with outline
 * level N-1; null when the fragment has neither.
 */
export function headingStyleId(level) {
  const styles = Object.entries(context.presentation?.styles?.paragraph ?? {})
  const named = styles.find(([, style]) => (style.name ?? '').toLowerCase() === `heading ${level}`)
  if (named) return named[0]
  return styles.find(([, style]) => style.outlineLvl === level - 1)?.[0] ?? null
}

/** Page geometry (mm) the pagination plugin fits against, or null pre-load. */
export function pageSection() {
  return context.section
}

/** The whole presentation payload (sections, headersFooters, settings…). */
export function presentationPayload() {
  return context.presentation
}

/** Publishes a computed page layout to whoever registered onPageLayout. */
export function emitPageLayout(layout) {
  context.onPageLayout?.(layout)
}
